import React from 'react';

interface AIEngineIndicatorProps {
  status: 'active' | 'fallback' | 'unknown';
}

/**
 * Visual indicator showing whether the external AI engine is active
 * Displays in the top-right corner of the game room
 */
export const AIEngineIndicator: React.FC<AIEngineIndicatorProps> = ({ status }) => {
  if (status === 'unknown') {
    return null; // Don't show anything until first AI move
  }

  const getStatusConfig = () => {
    switch (status) {
      case 'active':
        return {
          icon: <span className="text-white" aria-hidden>⚡</span>,
          label: 'AI Engine Active',
          bgColor: 'bg-green-500/90',
          textColor: 'text-white',
          borderColor: 'border-green-400',
          pulseColor: 'bg-green-400',
          tooltip: 'External AI engine is running and making decisions'
        };
      case 'fallback':
        return {
          icon: <span className="text-white" aria-hidden>⏱️</span>,
          label: 'Built-in AI',
          bgColor: 'bg-amber-500/90',
          textColor: 'text-white',
          borderColor: 'border-amber-400',
          pulseColor: 'bg-amber-400',
          tooltip: 'Using built-in AI (external engine unavailable)'
        };
      default:
        return {
          icon: <span className="text-white" aria-hidden>❔</span>,
          label: 'Unknown',
          bgColor: 'bg-gray-500/90',
          textColor: 'text-white',
          borderColor: 'border-gray-400',
          pulseColor: 'bg-gray-400',
          tooltip: 'AI status unknown'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className="fixed top-4 right-4 z-50 group">
      <div
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg
          ${config.bgColor} ${config.textColor}
          border-2 ${config.borderColor}
          shadow-lg backdrop-blur-sm
          transition-all duration-300
          hover:scale-105
        `}
        title={config.tooltip}
      >
        {/* Pulse animation for active status */}
        {status === 'active' && (
          <span className="relative flex h-3 w-3">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.pulseColor} opacity-75`}></span>
            <span className={`relative inline-flex rounded-full h-3 w-3 ${config.pulseColor}`}></span>
          </span>
        )}
        
        {/* Icon */}
        <div className="flex items-center">
          {config.icon}
        </div>
        
        {/* Label */}
        <span className="text-sm font-semibold whitespace-nowrap">
          {config.label}
        </span>
      </div>
      
      {/* Tooltip on hover */}
      <div className="
        absolute top-full right-0 mt-2
        opacity-0 group-hover:opacity-100
        transition-opacity duration-200
        pointer-events-none
      ">
        <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-xl">
          {config.tooltip}
          <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
        </div>
      </div>
    </div>
  );
};

export default AIEngineIndicator;
